// @mui
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { CardProps } from '@mui/material/Card';
// hooks
import { useLocales } from 'src/locales';
// components
import Iconify from 'src/components/iconify';
import { usePopover } from 'src/components/custom-popover';
import FileThumbnail from 'src/components/file-thumbnail';
import ReportPopover from './report-popover';
//
import { IReportManager } from '../../types/report';
import { useRouter } from '../../routes/hook';
import { paths } from '../../routes/paths';

// ----------------------------------------------------------------------

interface Props extends CardProps {
  file: IReportManager;
  selected?: boolean;
  onSelect?: VoidFunction;
}

export default function ReportsItem({ file, selected, onSelect, sx, ...other }: Props) {
  const { t } = useLocales();
  const router = useRouter();
  const popover = usePopover();

  const renderAction = (
    <Box
      sx={{
        top: 8,
        right: 8,
        position: 'absolute',
      }}
    >
      <IconButton
        color={popover.open ? 'inherit' : 'default'}
        onClick={(e) => {
          e.stopPropagation();
          popover.onOpen(e);
        }}
      >
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>
    </Box>
  );

  const renderIcon = (
    <Box sx={{ position: 'relative' }}>
      <FileThumbnail file={file.type} sx={{ width: 36, height: 36 }} />
      {!file.editable ? (
        <Iconify
          sx={{
            position: 'absolute',
            bottom: 0,
            right: -6,
            color: (theme) => theme.palette.grey[600],
          }}
          icon="eva:lock-outline"
        />
      ) : (
        file.isGlobal && (
          <Iconify
            sx={{
              position: 'absolute',
              bottom: 0,
              right: -6,
              color: (theme) => theme.palette.grey[600],
            }}
            icon="eva:globe-2-outline"
          />
        )
      )}
    </Box>
  );

  const renderText = (
    <ListItemText
      primary={t(file.name)}
      secondary={t(file.description ?? '')}
      primaryTypographyProps={{
        noWrap: true,
        typography: 'subtitle2',
      }}
      secondaryTypographyProps={{
        mt: 0.5,
        component: 'span',
        alignItems: 'center',
        typography: 'caption',
        color: 'text.disabled',
        display: 'inline-flex',
      }}
    />
  );

  return (
    <>
      <Stack
        component={Paper}
        variant="outlined"
        alignItems="flex-start"
        onClick={() => router.push(paths.report.view(file.id))}
        sx={{
          pt: 2.5,
          pr: 2.5,
          pl: 2.5,
          pb: 2,
          borderRadius: 2,
          bgcolor: 'unset',
          cursor: 'pointer',
          position: 'relative',
          ...sx,
        }}
        spacing={2}
        {...other}
      >
        {renderIcon}

        {renderText}

        {file.editable && renderAction}
      </Stack>

      {file.editable && <ReportPopover id={file.id} popover={popover} />}
    </>
  );
}
